/**
 * Network configuration for multi-chain support
 * Defines supported networks for the E-Payment Stablecoin application
 */

export interface NetworkConfig {
  chainId: number;
  chainIdHex: string;
  name: string;
  displayName: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  rpcUrls: string[];
  blockExplorerUrls: string[];
  isTestnet: boolean;
  iconUrl?: string;
}

export interface SupportedNetworks {
  [chainId: number]: NetworkConfig;
}

/**
 * Supported network configurations
 */
export const SUPPORTED_NETWORKS: SupportedNetworks = {
  // Ethereum Mainnet
  1: {
    chainId: 1,
    chainIdHex: '0x1',
    name: 'ethereum',
    displayName: 'Ethereum',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    rpcUrls: [
      'https://cloudflare-eth.com',
      'https://mainnet.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161',
      'https://eth-mainnet.alchemyapi.io/v2/demo',
      'https://eth-mainnet.gateway.pokt.network/v1/5f3453978e354ab992c4da79'
    ],
    blockExplorerUrls: ['https://etherscan.io'],
    isTestnet: false,
    iconUrl: '/icons/ETH.svg'
  },

  // BSC Mainnet
  56: {
    chainId: 56,
    chainIdHex: '0x38',
    name: 'bsc',
    displayName: 'BNB Smart Chain',
    nativeCurrency: {
      name: 'BNB',
      symbol: 'BNB',
      decimals: 18,
    },
    rpcUrls: [
      'https://bsc-dataseed1.binance.org/',
      'https://bsc-dataseed2.binance.org/',
      'https://bsc-dataseed3.binance.org/',
      'https://bsc-dataseed4.binance.org/',
      'https://bsc-dataseed1.defibit.io/',
      'https://bsc-dataseed2.defibit.io/'
    ],
    blockExplorerUrls: ['https://bscscan.com'],
    isTestnet: false,
    iconUrl: '/icons/bnb.svg'
  },

  // BSC Testnet (for development)
  97: {
    chainId: 97,
    chainIdHex: '0x61',
    name: 'bsc-testnet',
    displayName: 'BSC Testnet',
    nativeCurrency: {
      name: 'Test BNB',
      symbol: 'tBNB',
      decimals: 18,
    },
    rpcUrls: [
      'https://data-seed-prebsc-1-s1.binance.org:8545/',
      'https://data-seed-prebsc-2-s1.binance.org:8545/',
      'https://data-seed-prebsc-1-s2.binance.org:8545/',
      'https://data-seed-prebsc-2-s2.binance.org:8545/'
    ],
    blockExplorerUrls: ['https://testnet.bscscan.com'],
    isTestnet: true,
    iconUrl: '/icons/bnb.svg'
  },

  // Ethereum Sepolia Testnet (replacing deprecated Rinkeby)
  11155111: {
    chainId: 11155111,
    chainIdHex: '0xaa36a7',
    name: 'sepolia',
    displayName: 'Sepolia Testnet',
    nativeCurrency: {
      name: 'Sepolia Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    rpcUrls: [
      'https://rpc.sepolia.org',
      'https://sepolia.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161',
      'https://eth-sepolia.g.alchemy.com/v2/demo'
    ],
    blockExplorerUrls: ['https://sepolia.etherscan.io'],
    isTestnet: true,
    iconUrl: '/icons/ETH.svg'
  },

  // Solana Mainnet
  9999: {
    chainId: 9999,
    chainIdHex: '0x270f',
    name: 'solana',
    displayName: 'Solana',
    nativeCurrency: {
      name: 'Solana',
      symbol: 'SOL',
      decimals: 9,
    },
    rpcUrls: [
      'https://api.mainnet-beta.solana.com',
      'https://solana-api.projectserum.com',
      'https://rpc.ankr.com/solana'
    ],
    blockExplorerUrls: ['https://solscan.io'],
    isTestnet: false,
    iconUrl: '/icons/sol.svg'
  }
};

/**
 * Default network configuration
 */
export const DEFAULT_NETWORK_ID = 1; // Ethereum Mainnet
export const SOLANA_NETWORK_ID = 9999;
export const BSC_NETWORK_ID = 56;
export const BSC_TESTNET_ID = 97;

/**
 * Get network configuration by chain ID
 */
export const getNetworkConfig = (chainId: number): NetworkConfig | undefined => {
  return SUPPORTED_NETWORKS[chainId];
};

/**
 * Get all mainnet networks
 */
export const getMainnetNetworks = (): NetworkConfig[] => {
  return Object.values(SUPPORTED_NETWORKS).filter(network => !network.isTestnet);
};

/**
 * Get all testnet networks
 */
export const getTestnetNetworks = (): NetworkConfig[] => {
  return Object.values(SUPPORTED_NETWORKS).filter(network => network.isTestnet);
};

/**
 * Check if a chain ID is supported
 */
export const isSupportedNetwork = (chainId: number): boolean => {
  return chainId in SUPPORTED_NETWORKS;
};

/**
 * Get network display name by chain ID
 */
export const getNetworkDisplayName = (chainId: number): string => {
  const network = getNetworkConfig(chainId);
  return network?.displayName || `Unknown Network (${chainId})`;
};

/**
 * Get primary RPC URL for a network
 */
export const getNetworkRpcUrl = (chainId: number): string | undefined => {
  const network = getNetworkConfig(chainId);
  return network?.rpcUrls[0];
};

/**
 * Get block explorer URL for a network
 */
export const getBlockExplorerUrl = (chainId: number): string | undefined => {
  const network = getNetworkConfig(chainId);
  return network?.blockExplorerUrls[0];
};

/**
 * Check if network is a testnet
 */
export const isTestnetNetwork = (chainId: number): boolean => {
  const network = getNetworkConfig(chainId);
  return network?.isTestnet || false;
};

/**
 * Network names mapping for easy reference
 */
export const NETWORK_NAMES = {
  1: 'ethereum',
  56: 'bsc',
  97: 'bsc-testnet',
  11155111: 'sepolia',
  9999: 'solana'
} as const;

/**
 * Reverse mapping from name to chain ID
 */
export const NETWORK_CHAIN_IDS = {
  ethereum: 1,
  bsc: 56,
  'bsc-testnet': 97,
  sepolia: 11155111,
  solana: 9999
} as const;

/**
 * Get all supported chain IDs
 */
export const getSupportedChainIds = (): number[] => {
  return Object.keys(SUPPORTED_NETWORKS).map(Number);
};

/**
 * Get production networks (mainnet only)
 */
export const getProductionNetworks = (): NetworkConfig[] => {
  return [SUPPORTED_NETWORKS[1], SUPPORTED_NETWORKS[56], SUPPORTED_NETWORKS[9999]];
};